import { Action } from "redux";
import { ThunkAction } from "redux-thunk";
import { endpoints } from "../Constants/endpoints";
import { IAppState, ApiRequest } from "../../Common";
import { Workflow } from "../../Workflows/Models/Workflow";

interface ITopicWorkflowListAction extends Action {
  type: TopicWorkflowActionType;
  payload: any;
}

// ARGUMENT TYPES

export interface IGetTopicWorkflowsArguments {}
export interface IUnloadTopicWorkflowsArguments {}
export interface ISetTopicWorkflowsLoadingArguments {}
export interface IUpdateTopicWorkflowListItemArguments {
  updatedWorkflow: Workflow;
}
export interface ISetTopicWorkflowsErroredArguments {
  errorMessage: string;
  errorCode: number;
}
export interface IWorkflowListRemoveWorkflowFromListArguments {
  workflowCode: string;
}

// PAYLOAD TYPES

// Payload for the TOPIC_WORKFLOW_LIST_LOADED action, contains the loaded workflows
export interface ITopicWorkflowsListLoadedPayload {
  loadedWorkflows: Workflow[];
}

// Payload for the TOPIC_WORKFLOW_LIST_UNLOAD action
export interface IUnloadTopicWorkflowsPayload {}

// Payload for the TOPIC_WORKFLOW_LIST_SET_LOADING action
export interface ISetTopicWorkflowsLoadingPayload {}

// Payload for the TOPIC_WORKFLOW_LIST_SET_ERRORED action
export interface ISetTopicWorkflowsErroredPayload {
  errorMessage: string;
  errorCode: number;
}

// Payload for the TOPIC_WORKFLOW_LIST_UPDATE_LIST_ITEM action
export interface IWorkflowListUpdateListItemPayload {
  updatedWorkflow: Workflow;
}

// Payload for the TOPIC_WORKFLOW_LIST_REMOVE_ITEM action
export interface IWorkflowListRemoveWorkflowFromListPayload {
  workflowCode: string;
}

// ACTION TYPES

// Action for the TOPIC_WORKFLOW_LIST_LOADED action
export interface ITopicWorkflowListLoadedAction extends ITopicWorkflowListAction {
  payload: ITopicWorkflowsListLoadedPayload;
}

// Action for the TOPIC_WORKFLOW_LIST_UNLOAD action
export interface IUnloadTopicWorkflowsAction extends ITopicWorkflowListAction {
  payload: IUnloadTopicWorkflowsPayload;
}

// Action for the TOPIC_WORKFLOW_LIST_SET_LOADING action
export interface ISetTopicWorkflowsLoadingAction extends ITopicWorkflowListAction {
  payload: ISetTopicWorkflowsLoadingPayload;
}

// Action for the TOPIC_WORKFLOW_LIST_SET_ERRORED action
export interface ISetTopicWorkflowsErroredAction extends ITopicWorkflowListAction {
  payload: ISetTopicWorkflowsErroredPayload;
}

// Action for the TOPIC_WORKFLOW_LIST_UPDATE_LIST_ITEM action
export interface IWorkflowListUpdateListItemAction extends ITopicWorkflowListAction {
  payload: IWorkflowListUpdateListItemPayload;
}

// Action for the TOPIC_WORKFLOW_LIST_REMOVE_ITEM action
export interface IWorkflowListRemoveListItemAction extends ITopicWorkflowListAction {
  payload: IWorkflowListRemoveWorkflowFromListPayload;
}

// Union of all action types. If you define a new action type, add it here...
export type TopicWorkflowAction =
  | ITopicWorkflowListLoadedAction
  | IUnloadTopicWorkflowsAction
  | ISetTopicWorkflowsLoadingAction
  | ISetTopicWorkflowsErroredAction
  | IWorkflowListUpdateListItemAction
  | IWorkflowListRemoveListItemAction;

export enum TopicWorkflowActionType {
  TOPIC_WORKFLOW_LIST_LOADED = "TOPIC_WORKFLOW_LIST_LOADED",
  TOPIC_WORKFLOW_LIST_UNLOAD = "TOPIC_WORKFLOW_LIST_UNLOAD",
  TOPIC_WORKFLOW_LIST_SET_LOADING = "TOPIC_WORKFLOW_LIST_SET_LOADING",
  TOPIC_WORKFLOW_LIST_SET_ERRORED = "TOPIC_WORKFLOW_LIST_SET_ERRORED",
  TOPIC_WORKFLOW_LIST_UPDATE_LIST_ITEM = "TOPIC_WORKFLOW_LIST_UPDATE_LIST_ITEM",
  TOPIC_WORKFLOW_LIST_REMOVE_ITEM = "TOPIC_WORKFLOW_LIST_REMOVE_ITEM"
}

export const setErrored = (
  args: ISetTopicWorkflowsErroredArguments
): ISetTopicWorkflowsErroredAction => ({
  type: TopicWorkflowActionType.TOPIC_WORKFLOW_LIST_SET_ERRORED,
  payload: { ...args }
});

// Gets topic workflows from the server asynchronously and dispatches a TOPIC_WORKFLOW_LIST_LOADED action when complete, or a TOPIC_WORKFLOW_LIST_SET_ERRORED action if the get failed
export const getWorkflows = (
  args: IGetTopicWorkflowsArguments
): ThunkAction<
  void,
  IAppState,
  {},
  ITopicWorkflowListLoadedAction | ISetTopicWorkflowsErroredAction
> => dispatch => {
  ApiRequest.fromPath(endpoints.topicWorkflows.getAll())
    .withAuthHeader()
    .get()
    .then(response => response.json())
    .then(json => {
      if (json.status === undefined) {
        // this was successful
        dispatch({
          type: TopicWorkflowActionType.TOPIC_WORKFLOW_LIST_LOADED,
          payload: {
            loadedWorkflows: json.payload
          }
        });
      } else {
        // this failed
        dispatch(
          setErrored({
            errorMessage: json.error.message,
            errorCode: json.status
          })
        );
      }
    });
};

export const unloadWorkflows = (
  args: IUnloadTopicWorkflowsArguments
): IUnloadTopicWorkflowsAction => ({
  type: TopicWorkflowActionType.TOPIC_WORKFLOW_LIST_UNLOAD,
  payload: { ...args }
});

export const setLoading = (
  args: ISetTopicWorkflowsLoadingArguments
): ISetTopicWorkflowsLoadingAction => ({
  type: TopicWorkflowActionType.TOPIC_WORKFLOW_LIST_SET_LOADING,
  payload: { ...args }
});

export const updateListItem = (
  args: IUpdateTopicWorkflowListItemArguments
): IWorkflowListUpdateListItemAction => ({
  type: TopicWorkflowActionType.TOPIC_WORKFLOW_LIST_UPDATE_LIST_ITEM,
  payload: { ...args }
});

export const removeItemFromList = (
  args: IWorkflowListRemoveWorkflowFromListArguments
): IWorkflowListRemoveListItemAction => ({
  type: TopicWorkflowActionType.TOPIC_WORKFLOW_LIST_REMOVE_ITEM,
  payload: { ...args }
});
